/**
 * FinancingPlanTable Component
 *
 * Section 2 of the Gestão de Recursos Edit Page: "Plano de Financiamento"
 * Displays table with 18 pre-defined funding sources + total
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Table headers: #F6F6F6 (14px bold)
 * - Table borders: #5D5D5D
 * - Total row: Success green #13D480
 * - Uses existing CurrencyInput for money fields
 * - Uses existing Input for text observations
 *
 * 18 Funding Sources (Lei 8.685/1993):
 * 1. Art 1º (Investment), 2. Art 1º-A (Sponsorship), 3. Art 3º (Co-production)
 * 4. Art 3º-A, 5. FUNCINES, 6-7. Individual/Corporate Sponsorship
 * 8-10. Federal/State/Municipal Public, 11-12. State/Municipal Incentive Laws
 * 13. Private Sponsorship, 14-15. International Prize/Program
 * 16. Exhibition License, 17. Non-financial Contribution, 18. Other
 */

import { UseFormReturn } from "react-hook-form";
import { Controller, useFieldArray } from "react-hook-form";
import CurrencyInput from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import { FinancingPlanItem } from "@/types/gestao-recursos.interface";
import { GestaoRecursosEditForm } from "@/pages/financeiro/gestao-recursos-editar";

// 18 pre-defined funding sources from ANCINE regulations
const FUNDING_SOURCES = [
  "Lei nº 8.685/93 - art 1º",
  "Lei nº 8.685/93 - art 1º-A",
  "Lei nº 8.685/93 - art 3º",
  "Lei nº 8.685/93 - art 3º-A",
  "Contribuição de FUNCINES (investimento)",
  "Patrocínio Pessoa Física",
  "Patrocínio Pessoa Jurídica",
  "Público - Federal",
  "Público - Estadual",
  "Público - Municipal",
  "Lei Estadual de Incentivo",
  "Lei Municipal de Incentivo",
  "Patrocínio Privado",
  "Prêmio Internacional",
  "Programa Internacional",
  "Licença de Exibição",
  "Contribuição não financeira",
  "Outros",
];

interface FinancingPlanTableProps {
  form: UseFormReturn<GestaoRecursosEditForm>;
}

export function FinancingPlanTable({ form }: FinancingPlanTableProps) {
  const { fields } = useFieldArray({
    control: form.control,
    name: "financingPlan",
  });

  // Watch all values to calculate total
  const watchedItems = form.watch("financingPlan");

  // Calculate total funding
  const totalFunding = watchedItems?.reduce(
    (sum, item) => sum + (item?.valor || 0),
    0
  ) || 0;

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[40px]">
      {/* Section Title */}
      <h3 className="text-[20px] font-bold text-[#F6F6F6] mb-[30px]">
        Plano de Financiamento
      </h3>

      {/* Table */}
      <div className="border border-[#5D5D5D] rounded-[10px] overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-[2fr_1.5fr_2fr] bg-[#0C1427] border-b border-[#5D5D5D]">
          <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6] border-r border-[#5D5D5D]">
            Fonte de Financiamento
          </div>
          <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6] border-r border-[#5D5D5D]">
            Valor
          </div>
          <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6]">
            Observações
          </div>
        </div>

        {/* Table Rows */}
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="grid grid-cols-[2fr_1.5fr_2fr] border-b border-[#5D5D5D] last:border-b-0"
          >
            {/* Funding Source (read-only label) */}
            <div className="px-[20px] py-[16px] text-[14px] text-[#F6F6F6] border-r border-[#5D5D5D] flex items-center">
              {FUNDING_SOURCES[index]}
            </div>

            {/* Value (CurrencyInput) */}
            <div className="px-[20px] py-[12px] border-r border-[#5D5D5D]">
              <Controller
                name={`financingPlan.${index}.valor`}
                control={form.control}
                render={({ field }) => (
                  <CurrencyInput
                    field={field}
                    placeholder="R$ 0,00"
                    className="text-sm"
                  />
                )}
              />
            </div>

            {/* Observations (Text Input) */}
            <div className="px-[20px] py-[12px]">
              <Controller
                name={`financingPlan.${index}.observacoes`}
                control={form.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Adicionar observação..."
                    className="bg-transparent border-none text-[#F6F6F6] text-sm focus-visible:ring-0 px-0"
                  />
                )}
              />
            </div>
          </div>
        ))}

        {/* Total Row */}
        <div className="grid grid-cols-[2fr_1.5fr_2fr] bg-[#0C1427]">
          <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#13D480] border-r border-[#5D5D5D]">
            Total do Plano de Financiamento
          </div>
          <div className="px-[20px] py-[12px] border-r border-[#5D5D5D]">
            <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-[#13D480] font-bold text-sm">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(totalFunding)}
            </div>
          </div>
          <div className="px-[20px] py-[16px]"></div>
        </div>
      </div>
    </div>
  );
}
